import React from 'react';
import { sortSuccessPatterns, sortAvoidPatterns, capitalizeCategory } from './utils/patternSort';
import { formatModelName } from './utils/modelName';
import { AI_PROVIDER } from './constants/ai';

/**
 * AI Result Card Component - Displays structured AI analysis
 * Sections: Overall Summary, Win Analysis, Loss Analysis, Actionable Recommendations
 *
 * Shared by AiReviewDialog (during generation) and ReviewDetailDialog (existing
 * reviews + just-generated preview). Extracted from AiReviewDialog so both
 * entry points render identically.
 */
function AiResultCard({ result, provider }) {
  // Model id stored in the result (each review/scan embeds the exact model
  // that produced it). Legacy data without a model falls back to the plain
  // provider name — NOT a build-time env name, which under the dual-model
  // trial could claim the wrong model entirely.
  const providerName = formatModelName(result.model)
    || (provider === AI_PROVIDER.ANTHROPIC ? 'Claude' : 'GPT');
  const providerClass = provider === AI_PROVIDER.ANTHROPIC ? 'claude' : 'gpt';

  if (result.error) {
    return (
      <div className={`ai-result-card ${providerClass}`}>
        <div className="ai-result-header">
          <h4>{providerName}</h4>
        </div>
        <div className="ai-result-content">
          <div className="error-state">
            <div className="error-icon">❌</div>
            <p className="error-message">Error: {result.error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`ai-result-card ${providerClass}`}>
      <div className="ai-result-header">
        <h4>{providerName}</h4>
      </div>
      <div className="ai-result-content">
        {(result.overallSummary || result.summary) && (
          <div className="ai-result-section overall-summary">
            <h5>📊 Overall Summary</h5>
            <p className="summary-text">{result.overallSummary || result.summary}</p>
            {result.performanceMetrics && (
              <div className="performance-metrics">
                <span className="metric">
                  Win Rate: <strong>{result.performanceMetrics.winRate}%</strong>
                </span>
                <span className="metric">
                  Avg Win: <strong className="profit">+${result.performanceMetrics.avgWin?.toFixed(2) || 'N/A'}</strong>
                </span>
                <span className="metric">
                  Avg Loss: <strong className="loss">-${Math.abs(result.performanceMetrics.avgLoss || 0).toFixed(2)}</strong>
                </span>
              </div>
            )}
          </div>
        )}

        {(result.winAnalysis || (result.winningPatterns && result.winningPatterns.length > 0)) && (
          <div className="ai-result-section win-analysis">
            <h5>✅ Win Analysis</h5>
            {result.winAnalysis?.summary && (
              <p className="section-summary">{result.winAnalysis.summary}</p>
            )}
            <div className="patterns-list">
              <h6>Success Patterns:</h6>
              <ul>
                {sortSuccessPatterns(result.winAnalysis?.successPatterns || result.winningPatterns || []).map((pattern, index) => (
                  <li key={index} className="pattern-item success">
                    {pattern.category && (
                      <div className="pattern-badges">
                        <span className={`cat-badge cat-${pattern.category}`}>
                          {capitalizeCategory(pattern.category)}
                        </span>
                      </div>
                    )}
                    <div className="pattern-header">
                      <strong>{pattern.pattern || pattern.trend || pattern.name}</strong>
                      <span className="pattern-stats">
                        {pattern.winRate}% win rate • {pattern.trades} trades
                      </span>
                    </div>
                    {pattern.description && (
                      <p className="pattern-description">{pattern.description}</p>
                    )}
                    {pattern.keyFactors && pattern.keyFactors.length > 0 && (
                      <div className="key-factors">
                        <span className="factors-label">Key Factors:</span>
                        {pattern.keyFactors.map((factor, i) => (
                          <span key={i} className="factor-tag">{factor}</span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {(result.lossAnalysis || (result.losingPatterns && result.losingPatterns.length > 0)) && (
          <div className="ai-result-section loss-analysis">
            <h5>❌ Loss Analysis</h5>
            {result.lossAnalysis?.summary && (
              <p className="section-summary">{result.lossAnalysis.summary}</p>
            )}
            <div className="patterns-list">
              <h6>Loss Patterns:</h6>
              <ul>
                {sortAvoidPatterns(result.lossAnalysis?.lossPatterns || result.losingPatterns || []).map((pattern, index) => (
                  <li key={index} className="pattern-item loss">
                    {pattern.category && (
                      <div className="pattern-badges">
                        <span className={`cat-badge cat-${pattern.category}`}>
                          {capitalizeCategory(pattern.category)}
                        </span>
                      </div>
                    )}
                    <div className="pattern-header">
                      <strong>{pattern.pattern || pattern.trend || pattern.name}</strong>
                      <span className="pattern-stats">
                        {pattern.winRate}% win rate • {pattern.trades} trades
                      </span>
                    </div>
                    {pattern.description && (
                      <p className="pattern-description">{pattern.description}</p>
                    )}
                    {pattern.rootCause && (
                      <div className="root-cause">
                        <span className="cause-label">🔍 Root Cause:</span>
                        <span className="cause-text">{pattern.rootCause}</span>
                      </div>
                    )}
                    {pattern.howToAvoid && (
                      <div className="how-to-avoid">
                        <span className="avoid-label">🛡️ How to Avoid:</span>
                        <span className="avoid-text">{pattern.howToAvoid}</span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            {result.lossAnalysis?.commonMistakes && result.lossAnalysis.commonMistakes.length > 0 && (
              <div className="common-mistakes">
                <h6>⚠️ Common Mistakes:</h6>
                <ul>
                  {result.lossAnalysis.commonMistakes.map((mistake, index) => (
                    <li key={index}>{mistake}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {(result.actionableRecommendations || result.recommendations) && (
          <div className="ai-result-section actionable-recommendations">
            <h5>💡 Actionable Recommendations</h5>
            <ul className="recommendations-list">
              {(result.actionableRecommendations || result.recommendations || []).map((rec, index) => {
                const recText = typeof rec === 'string' ? rec : rec.text || rec.recommendation;
                const priority = typeof rec === 'object' ? rec.priority : null;
                const category = typeof rec === 'object' ? rec.category : null;

                return (
                  <li key={index} className={`recommendation-item ${priority || ''}`}>
                    {priority && (
                      <span className={`priority-badge ${priority}`}>
                        {priority === 'high' ? '🔴' : priority === 'medium' ? '🟡' : '🟢'} {priority}
                      </span>
                    )}
                    {category && <span className="category-tag">{category}</span>}
                    <span className="rec-text">{recText}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {result.keyLessons && result.keyLessons.length > 0 && !result.actionableRecommendations && (
          <div className="ai-result-section">
            <h5>📚 Key Lessons</h5>
            <ul>
              {result.keyLessons.map((lesson, index) => (
                <li key={index}>{lesson}</li>
              ))}
            </ul>
          </div>
        )}

        {result.aiAccuracy && (
          <div className="ai-result-section ai-accuracy">
            <h5>🎯 AI Accuracy</h5>
            <div className="accuracy-grid">
              <div className="accuracy-item">
                <span className="accuracy-label">Claude</span>
                <span className="accuracy-value">{result.aiAccuracy.claudeWinRate}%</span>
              </div>
              <div className="accuracy-item">
                <span className="accuracy-label">GPT</span>
                <span className="accuracy-value">{result.aiAccuracy.gptWinRate}%</span>
              </div>
              <div className="accuracy-item">
                <span className="accuracy-label">Consensus</span>
                <span className="accuracy-value">{result.aiAccuracy.bothAIWinRate}%</span>
              </div>
            </div>
          </div>
        )}

        {result.inputTokens && result.outputTokens && (
          <div className="token-count">
            Tokens: {result.inputTokens} in / {result.outputTokens} out
          </div>
        )}
      </div>
    </div>
  );
}

export default AiResultCard;
